// Admin: controla QUEM pode gerar token da extensão Chrome (gate de acesso).
// off = ninguém | allowlist = só emails listados | all = todos.
// [[project-chrome-extension-assistant]]

import { useEffect, useState } from "preact/hooks";
import { Loader2, Puzzle, Save } from "lucide-react";
import { api, type ApiTokenAccess } from "./api";
import { friendlyError } from "@app/ui/lib/friendly-error";

const MODES: Array<{ value: ApiTokenAccess["mode"]; label: string; desc: string }> = [
  { value: "off", label: "Desligado", desc: "Ninguém pode gerar token." },
  { value: "allowlist", label: "Allowlist", desc: "Só os emails listados." },
  { value: "all", label: "Todos", desc: "Qualquer usuário logado." }
];

export function ExtensionAccessView() {
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<ApiTokenAccess["mode"]>("off");
  const [allowText, setAllowText] = useState("");
  const [ttlHours, setTtlHours] = useState(24);
  const [maxDevices, setMaxDevices] = useState(1);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    try {
      const r = await api.apiTokenAccessGet();
      setMode(r.access.mode);
      setAllowText(r.access.allowlist.join("\n"));
      setTtlHours(r.access.ttlHours);
      setMaxDevices(r.access.maxDevices);
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
  }, []);

  async function save() {
    setBusy(true);
    setErr(null);
    setMsg(null);
    const allowlist = allowText
      .split(/[\n,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (mode === "all" && !confirm("Liberar a extensão para TODOS os usuários?")) {
      setBusy(false);
      return;
    }
    try {
      const r = await api.apiTokenAccessSet(mode, allowlist, {
        ttlHours,
        maxDevices
      });
      setMode(r.access.mode);
      setAllowText(r.access.allowlist.join("\n"));
      setTtlHours(r.access.ttlHours);
      setMaxDevices(r.access.maxDevices);
      setMsg("Salvo.");
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div class="flex items-center gap-2 p-6 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Carregando…
      </div>
    );
  }

  return (
    <div class="mx-auto max-w-2xl space-y-5 p-6">
      <div class="flex items-center gap-2">
        <Puzzle className="size-5 text-violet-600 dark:text-violet-400" />
        <h1 class="text-lg font-bold">Acesso à extensão Chrome</h1>
      </div>
      <p class="text-sm text-muted-foreground">
        Controla quem pode gerar o token da extensão (em Minha Conta → Acesso de
        aplicativos). O token dá acesso só aos endpoints de IA.
      </p>

      {err && <p class="text-sm text-destructive">{err}</p>}
      {msg && <p class="text-sm text-emerald-600 dark:text-emerald-400">{msg}</p>}

      <div class="space-y-2">
        {MODES.map((m) => (
          <button
            key={m.value}
            type="button"
            onClick={() => setMode(m.value)}
            class={
              "flex w-full items-start gap-3 rounded-md border p-3 text-left transition-colors " +
              (mode === m.value
                ? "border-violet-500 bg-violet-500/10"
                : "border-border hover:bg-accent")
            }
          >
            <span
              class={
                "mt-0.5 size-4 shrink-0 rounded-full border-2 " +
                (mode === m.value
                  ? "border-violet-500 bg-violet-500"
                  : "border-muted-foreground")
              }
            />
            <span class="min-w-0">
              <span class="block text-sm font-medium">{m.label}</span>
              <span class="block text-xs text-muted-foreground">{m.desc}</span>
            </span>
          </button>
        ))}
      </div>

      {mode === "allowlist" && (
        <div class="space-y-1">
          <label class="text-xs font-medium text-muted-foreground">
            Emails liberados (um por linha)
          </label>
          <textarea
            value={allowText}
            rows={6}
            onInput={(e) => setAllowText((e.target as HTMLTextAreaElement).value)}
            placeholder={"luis@exemplo.com\noutro@exemplo.com"}
            class="w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-xs"
          />
        </div>
      )}

      <div class="grid grid-cols-2 gap-4">
        <div class="space-y-1">
          <label class="text-xs font-medium text-muted-foreground">
            Duração da sessão (horas)
          </label>
          <input
            type="number"
            title="Duração da sessão (horas)"
            min={1}
            max={720}
            value={ttlHours}
            onInput={(e) =>
              setTtlHours(Number((e.target as HTMLInputElement).value) || 24)
            }
            class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          />
          <p class="text-[11px] text-muted-foreground">
            Token expira após isso e a extensão pede reconexão. Padrão: 24h.
          </p>
        </div>
        <div class="space-y-1">
          <label class="text-xs font-medium text-muted-foreground">
            Dispositivos por usuário
          </label>
          <input
            type="number"
            title="Dispositivos por usuário"
            min={1}
            max={10}
            value={maxDevices}
            onInput={(e) =>
              setMaxDevices(Number((e.target as HTMLInputElement).value) || 1)
            }
            class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          />
          <p class="text-[11px] text-muted-foreground">
            Tokens ativos ao mesmo tempo. Acima disso, o mais antigo é revogado.
          </p>
        </div>
      </div>

      <button
        type="button"
        onClick={() => void save()}
        disabled={busy}
        class="inline-flex items-center gap-1.5 rounded-md bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
      >
        {busy ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
        Salvar
      </button>
    </div>
  );
}
